import { useState } from 'react';
import {
  RiAddLine,
  RiLock2Line,
  RiShieldCheckLine,
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiDeleteBin6Line,
  RiExternalLinkLine,
} from 'react-icons/ri';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useStore } from '../../state/store';
import { api } from '../../state/api';
import type { IntegrityReport } from '../../state/domain';
import {
  admiraltyLevel,
  INFO_CREDIBILITY_LABEL,
  INFO_CREDIBILITY_SHORT,
  SOURCE_RELIABILITY_LABEL,
  SOURCE_RELIABILITY_SHORT,
  type SourceReliability,
} from '../../state/domain';
import { cn, formatDate } from '@/lib/utils';

export function EvidencePanel() {
  const evidence = useStore(state => state.evidence);
  const activeTargetId = useStore(state => state.activeTargetId);
  const addEvidence = useStore(state => state.addEvidence);
  const deleteEvidence = useStore(state => state.deleteEvidence);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [integrity, setIntegrity] = useState<IntegrityReport | null>(null);
  const [verifying, setVerifying] = useState(false);
  const activeEngagementId = useStore(state => state.activeEngagementId);
  const [form, setForm] = useState({
    kind: 'manual',
    title: '',
    url: '',
    content: '',
    sourceReliability: 'C' as SourceReliability,
    infoCredibility: 3,
    location: '',
    witness: '',
    seizedAt: '',
  });

  return (
    <div className="flex flex-col gap-2.5 p-3">
      <div className="flex items-center justify-between">
        <span className="label-caps">证据链</span>
        <Badge variant="muted">{evidence.length} 条 · Admiralty 分级</Badge>
      </div>

      <div className="rounded-[var(--radius)] border border-border bg-card p-2.5">
        <div className="flex items-center gap-1.5">
          <RiLock2Line className="size-3.5 text-[var(--color-evidence)]" />
          <span className="text-[11.5px] font-medium">防篡改链</span>
          {integrity && (
            <Badge variant={integrity.ok ? 'evidence' : 'cord'} className="ml-auto">
              {integrity.ok ? '校验通过' : `${integrity.broken.length} 处异常`}
            </Badge>
          )}
        </div>
        <p className="mt-1 text-[10px] leading-relaxed text-muted-foreground">
          每条证据按序链接（sha256 前链 + 规范化载荷）。任何字段被事后修改都会在校验中暴露。
        </p>
        {integrity && !integrity.ok && (
          <div className="mt-1.5 flex flex-col gap-1">
            {integrity.broken.slice(0, 4).map(item => (
              <div key={item.id} className="flex items-center gap-1.5 text-[10px]">
                <span className="size-1.5 shrink-0 rounded-full bg-[var(--color-cord)]" />
                <span className="truncate">{item.title ?? item.id.slice(0, 10)}</span>
                <span className="shrink-0 text-muted-foreground">{item.reason}</span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-1.5 flex gap-1.5">
          <Button
            size="xs"
            variant="outline"
            disabled={verifying || !activeEngagementId}
            onClick={async () => {
              if (!activeEngagementId) return;
              setVerifying(true);
              try {
                setIntegrity(await api.verifyEvidenceChain(activeEngagementId));
              } catch (error) {
                useStore.getState().toast('error', (error as Error).message);
              } finally {
                setVerifying(false);
              }
            }}
          >
            <RiShieldCheckLine className={cn('size-3', verifying && 'animate-pulse')} />
            校验完整性
          </Button>
          <Button
            size="xs"
            variant="ghost"
            disabled={verifying || !activeEngagementId}
            onClick={async () => {
              if (!activeEngagementId) return;
              setVerifying(true);
              try {
                setIntegrity(await api.verifyEvidenceChain(activeEngagementId, true));
                useStore.getState().toast('success', '已为历史证据补齐链');
              } catch (error) {
                useStore.getState().toast('error', (error as Error).message);
              } finally {
                setVerifying(false);
              }
            }}
          >
            补齐历史链
          </Button>
        </div>
      </div>

      <details className="rounded-[var(--radius)] border border-border bg-card p-2.5">
        <summary className="cursor-pointer text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <RiAddLine className="size-3.5" />
            登记证据
          </span>
        </summary>
        <div className="mt-2 flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label>来源类型</Label>
              <Input value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })} />
            </div>
            <div className="flex flex-col gap-1">
              <Label>来源可靠性</Label>
              <select
                className="h-8 w-full rounded-[var(--radius-sm)] border border-input bg-card px-2 text-[12px]"
                value={form.sourceReliability}
                onChange={e => setForm({ ...form, sourceReliability: e.target.value as SourceReliability })}
              >
                {Object.entries(SOURCE_RELIABILITY_LABEL).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2 flex flex-col gap-1">
              <Label>信息可信度</Label>
              <select
                className="h-8 w-full rounded-[var(--radius-sm)] border border-input bg-card px-2 text-[12px]"
                value={form.infoCredibility}
                onChange={e => setForm({ ...form, infoCredibility: Number(e.target.value) })}
              >
                {Object.entries(INFO_CREDIBILITY_LABEL).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <Label>标题</Label>
          <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          <Label>来源 URL</Label>
          <Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label>提取地点</Label>
              <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
            </div>
            <div className="flex flex-col gap-1">
              <Label>提取时间</Label>
              <Input
                type="datetime-local"
                value={form.seizedAt}
                onChange={e => setForm({ ...form, seizedAt: e.target.value })}
              />
            </div>
          </div>
          <Label>见证人 / 提取人</Label>
          <Input value={form.witness} onChange={e => setForm({ ...form, witness: e.target.value })} />
          <Label>内容</Label>
          <Textarea rows={3} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} />
          <Button
            size="sm"
            onClick={async () => {
              await addEvidence({
                kind: form.kind,
                title: form.title,
                url: form.url || null,
                content: form.content,
                sourceReliability: form.sourceReliability,
                infoCredibility: form.infoCredibility,
                reliability: form.sourceReliability,
                location: form.location || null,
                witness: form.witness || null,
                seizedAt: form.seizedAt ? new Date(form.seizedAt).toISOString() : null,
                targetId: activeTargetId,
              });
              setForm({
                kind: 'manual',
                title: '',
                url: '',
                content: '',
                sourceReliability: 'C',
                infoCredibility: 3,
                location: '',
                witness: '',
                seizedAt: '',
              });
            }}
          >
            归档
          </Button>
        </div>
      </details>

      <div className="flex flex-col gap-1.5">
        {evidence.map(item => {
          const open = expanded === item.id;
          return (
            <div key={item.id} className="overflow-hidden rounded-[var(--radius)] border border-border bg-card">
              <button
                type="button"
                onClick={() => setExpanded(open ? null : item.id)}
                className="flex w-full items-center gap-2 px-2.5 py-2 text-left hover:bg-accent/40"
              >
                {open ? (
                  <RiArrowDownSLine className="size-3.5 shrink-0 text-muted-foreground" />
                ) : (
                  <RiArrowRightSLine className="size-3.5 shrink-0 text-muted-foreground" />
                )}
                {(() => {
                  const level = admiraltyLevel(item.sourceReliability, item.infoCredibility);
                  return (
                    <Badge
                      variant={level === 'high' ? 'evidence' : level === 'medium' ? 'assess' : 'cord'}
                      className="shrink-0 font-mono"
                      title={`来源 ${SOURCE_RELIABILITY_SHORT[item.sourceReliability]} · 信息 ${INFO_CREDIBILITY_SHORT[item.infoCredibility]}`}
                    >
                      {item.sourceReliability}
                      {item.infoCredibility}
                    </Badge>
                  );
                })()}
                <span className="hidden shrink-0 text-[9.5px] text-muted-foreground xl:inline">
                  {SOURCE_RELIABILITY_SHORT[item.sourceReliability]}
                </span>
                <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{item.kind}</span>
                <span className="min-w-0 flex-1 truncate text-[11.5px]">{item.title ?? item.url ?? '(无标题)'}</span>
              </button>
              {open && (
                <div className="flex flex-col gap-2 border-t border-border p-2.5">
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10.5px]">
                    <span className="text-muted-foreground">评级（来源 × 信息）</span>
                    <span>
                      {item.sourceReliability}
                      {item.infoCredibility} · {SOURCE_RELIABILITY_SHORT[item.sourceReliability]} /{' '}
                      {INFO_CREDIBILITY_SHORT[item.infoCredibility]}
                    </span>
                    <span className="text-muted-foreground">采集时间</span>
                    <span>{formatDate(item.collectedAt)}</span>
                    {item.seizedAt && (
                      <>
                        <span className="text-muted-foreground">提取时间</span>
                        <span>{formatDate(item.seizedAt)}</span>
                      </>
                    )}
                    {item.location && (
                      <>
                        <span className="text-muted-foreground">提取地点</span>
                        <span>{item.location}</span>
                      </>
                    )}
                    {item.witness && (
                      <>
                        <span className="text-muted-foreground">见证人</span>
                        <span>{item.witness}</span>
                      </>
                    )}
                  </div>
                  {item.url && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 truncate text-[10.5px] text-primary hover:underline"
                    >
                      <RiExternalLinkLine className="size-3 shrink-0" />
                      {item.url}
                    </a>
                  )}
                  <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-words rounded-[var(--radius-sm)] border border-border bg-muted/40 p-2 font-mono text-[10px] leading-relaxed">
                    {item.content?.slice(0, 6000) ?? '(无内容)'}
                  </pre>
                  {item.custody.length > 0 && (
                    <div className="flex flex-col gap-1">
                      <span className="label-caps">证据链（保管记录）</span>
                      <div className="flex flex-col gap-1">
                        {item.custody.map((entry, index) => (
                          <div key={index} className="flex items-center gap-2 text-[10px]">
                            <span className="size-1.5 rounded-full bg-[var(--color-evidence)]" />
                            <span className="text-muted-foreground">{formatDate(entry.at)}</span>
                            <span>{entry.action}</span>
                            <span className="text-muted-foreground">· {entry.actor}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {item.hash && (
                    <div className="truncate font-mono text-[9.5px] text-muted-foreground">sha256 {item.hash}</div>
                  )}
                  <Button
                    size="xs"
                    variant="outline"
                    className="self-start text-destructive"
                    onClick={() => deleteEvidence(item.id)}
                  >
                    <RiDeleteBin6Line className="size-3" />
                    删除证据
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {evidence.length === 0 && (
        <div className="rounded-[var(--radius)] border border-dashed border-border p-6 text-center text-[11.5px] text-muted-foreground">
          尚无证据。
        </div>
      )}
    </div>
  );
}